// ─────────────────────────────────────────
// reveal.ts — Scroll-triggered reveal animations
// Respects prefers-reduced-motion.
// Tauvior Landing Page
// ─────────────────────────────────────────
//
// O conteúdo nunca começa escondido. A versão anterior deixava os cards em
// opacity 0 até o IntersectionObserver disparar; onde ele não disparava
// (captura de página inteira, impressão, crawler, salto direto para uma
// âncora) sobrava o fundo da grade — um painel verde-acinzentado vazio no
// lugar dos cards de "Para quem" e da Grid. Agora a animação só roda no
// momento em que o card entra na tela vindo de baixo; antes disso ele já
// está visível, só fora da viewport.

import { prefersReducedMotion } from './utils';

/** Elements that should animate in when they enter the viewport */
const REVEAL_SELECTORS = [
  '.gc-card',
  '.aud-card',
  '.sobre-cred',
] as const;

const REVEAL_CLASS  = 'is-revealing';
const BASE_DELAY_MS = 80; // stagger delay per sibling index

/**
 * Anima cada card uma vez, quando ele cruza a borda de baixo da tela.
 * Cards que já estão na tela (ou acima dela) ao carregar não animam.
 */
function createObserver(): IntersectionObserver {
  return new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target as HTMLElement;
        observer.unobserve(el);

        // Entrou por cima (voltando a rolar) ou já estava na tela: sem animação.
        const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
        if (entry.boundingClientRect.top < rootBottom * 0.8) return;

        const siblings = Array.from(el.parentElement?.children ?? []);
        const index = siblings.indexOf(el);
        el.style.animationDelay = `${index * BASE_DELAY_MS}ms`;
        el.classList.add(REVEAL_CLASS);
        el.addEventListener('animationend', () => {
          el.classList.remove(REVEAL_CLASS);
          el.style.animationDelay = '';
        }, { once: true });
      });
    },
    { threshold: 0 }
  );
}

/**
 * Injects the CSS needed for the reveal animation.
 * `backwards` segura o quadro inicial só durante o atraso do escalonamento.
 */
function injectRevealStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes tvReveal {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: none; }
    }
    .${REVEAL_CLASS} { animation: tvReveal 0.55s ease backwards; }
  `;
  document.head.appendChild(style);
}

/**
 * Initialises the scroll-reveal system.
 * Sem IntersectionObserver ou com movimento reduzido, nada é animado —
 * e, como nada começa escondido, nada fica faltando.
 */
export function initReveal(): void {
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  injectRevealStyles();
  const observer = createObserver();
  document.querySelectorAll(REVEAL_SELECTORS.join(', ')).forEach((el) => observer.observe(el));
}
